import { useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, LogOut, Trash2, X } from 'lucide-react';
import {
  Button,
  Card,
  Chip,
  Dialog,
  EmptyState,
  FormField,
  Input,
  Skeleton,
  toast,
} from '@/ui';
import { env } from '@/env';
import { initialsOf } from '@/lib/initials';
import { useSessionStore } from '@/auth/session.store';
import { PASSWORD_MIN_LENGTH, PASSWORD_RULES } from '@/features/auth/password-policy';
import { authErrorMessage } from '@/features/auth/error-message';
import { refineLocation, type TrLocation } from '@/lib/locations';
import { LocationFields } from '@/features/locations/LocationFields';
import { useLocations } from '@/features/locations/useLocations';
import { PROFILE_FIELDS, deleteSelf, fetchSelf, updateSelf, type ProfileFieldKey } from './api';
import { profileKeys } from './query-keys';
import styles from './Profile.module.css';

/**
 * PROFIL — uc bolgenin de ayni ekrani (`customer.profile`, `exchange.profile`,
 * `admin.profile`).
 *
 * ISLETME BAGLANTI LISTESI YOK (2026-08-30). Ekranda bir zamanlar "Isletme"
 * karti vardi ve icinde Isletme bilgileri / Ekip / Raporlar / Islem gecmisi
 * baglantilari duruyordu; dordu de kenar cubugundaki menude ZATEN var, yani
 * kart menuyu tekrar ediyordu. Menu gruplandiktan sonra ("Yonetim" grubu) bu
 * tekrar tamamen gereksizlesti. Isletme ADI ile ROL rozeti kaldi — onlar
 * baglanti degil BILGI ve menude karsiliklari yok.
 *
 * Sonuc: bu ekran artik yetki sormuyor (isOrgAdmin/hasPermission cagrisi yok);
 * gorunurluk kararlari tek yerde, SidebarNav'da.
 *
 * GORUNUM (TEMA) KARTI DA YOK (2026-08-30). Tema secimi ust cubuktaki tek ikonlu
 * dugmede (app/ThemeToggle.tsx) ve HER EKRANDAN erisilebilir; profilde ikinci bir
 * kopya tutmak, iki kontrolun ayni depoyu farkli bicimlerde gostermesi demekti.
 *
 * Sifre alanlari OPSIYONEL. Doldurulursa sunucu KULLANICININ TUM OTURUMLARINI
 * DUSURUR; ekran bu yuzden kaydettikten sonra bilincli olarak cikis yapar
 * (asagida). Kullanicinin bunu ONCEDEN bilmesi icin de alanin yaninda uyari var.
 */

const makeProfileSchema = (t: TFunction, locations: readonly TrLocation[] = []) =>
  z
    .object({
      userFullName: z.string().trim().min(1, { message: t('validation.fullNameRequired') }),
      userEmail: z
        .string()
        .trim()
        .min(1, { message: t('validation.emailRequired') })
        .refine((value) => z.email().safeParse(value).success, {
          message: t('validation.emailInvalid'),
        }),
      userCity: z.string().trim().min(1, { message: t('validation.cityRequired') }),
      userDistrict: z.string().trim().min(1, { message: t('validation.districtRequired') }),
      userPhone: z.string().trim().min(1, { message: t('validation.phoneRequired') }),
      userIdentityId: z.string().trim().min(1, { message: t('validation.identityRequired') }),
      // BOS = "sifremi degistirme". Doluysa GRANULER kural listesi calisir ve
      // sunucuyla AYNI SIRADA ilk karsilanmayan kuralin mesaji doner; boylece
      // kullanici formda ve sunucuda ayni metni gorur.
      userPassword: z.string().superRefine((value, ctx) => {
        if (value === '') return;
        const failed = PASSWORD_RULES.find((rule) => !rule.test(value));
        if (!failed) return;
        ctx.addIssue({
          code: 'custom',
          message: t(failed.messageKey, { min: PASSWORD_MIN_LENGTH }),
        });
      }),
      confirmPassword: z.string(),
    })
    .superRefine((values, ctx) => {
      if (values.userPassword === '') return;
      if (values.confirmPassword === '') {
        ctx.addIssue({
          code: 'custom',
          path: ['confirmPassword'],
          message: t('auth.passwordRepeatRequired'),
        });
        return;
      }
      if (values.confirmPassword !== values.userPassword) {
        ctx.addIssue({
          code: 'custom',
          path: ['confirmPassword'],
          message: t('auth.passwordsMismatch'),
        });
      }
    })
    // Il/ilce listeden (M4): serbest metin donemi kalintisi bir deger kaydedilemez.
    .superRefine(
      refineLocation({
        locations,
        cityField: 'userCity',
        districtField: 'userDistrict',
        message: t('locations.pickFromList'),
      }),
    );

type ProfileFormValues = z.input<ReturnType<typeof makeProfileSchema>>;

const emptyFields = (): Record<ProfileFieldKey, string> => {
  const values = {} as Record<ProfileFieldKey, string>;
  for (const field of PROFILE_FIELDS) values[field.key] = '';
  return values;
};

export function ProfilePage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const snapshot = useSessionStore((state) => state.snapshot);
  const signOut = useSessionStore((state) => state.signOut);
  const revalidate = useSessionStore((state) => state.revalidate);

  const [deleteOpen, setDeleteOpen] = useState(false);

  const query = useQuery({
    queryKey: profileKeys.self(),
    queryFn: fetchSelf,
  });

  const locations = useLocations();
  const schema = useMemo(() => makeProfileSchema(t, locations), [t, locations]);

  // `values` icin KARARLI bir nesne: react-hook-form onu derin karsilastirmayla
  // izliyor, ama her render'da yeni bir nesne uretmek gereksiz is demek.
  const serverFields = query.data?.fields;
  const values = useMemo<ProfileFormValues | null>(
    () =>
      serverFields ? { ...serverFields, userPassword: '', confirmPassword: '' } : null,
    [serverFields],
  );

  const {
    control,
    register,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProfileFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { ...emptyFields(), userPassword: '', confirmPassword: '' },
    ...(values ? { values } : {}),
  });

  // `watch()` DEGIL `useWatch`: watch bir fonksiyon dondurur, React Compiler onu
  // guvenle hafizaya alamaz ve bayat arayuz uretebilir.
  const password = useWatch({ control, name: 'userPassword' }) ?? '';

  const save = useMutation({
    mutationFn: updateSelf,
    onSuccess: async (_result, variables) => {
      if (variables.userPassword !== null) {
        // SIFRE DEGISTI: sunucu tum oturumlari dusurdu. Ekranda kalmak, bir
        // sonraki istekte aciklamasiz bir 401'e ve giris ekranina firlamaya
        // esittir; cikisi BIZ yapiyoruz ki sebebi yazili olsun.
        toast({ message: t('profile.passwordChangedSignOut'), tone: 'success', duration: 8000 });
        await signOut();
        return;
      }
      toast({ message: t('auth.profileUpdated'), tone: 'success' });
      reset({ ...variables.fields, userPassword: '', confirmPassword: '' });
      await queryClient.invalidateQueries({ queryKey: profileKeys.self() });
      // Kabuktaki ad/e-posta oturum OZETINDEN geliyor; o da tazelenmeli.
      await revalidate();
    },
    onError: (error: unknown) => {
      toast({ message: authErrorMessage(error, t), tone: 'danger' });
    },
  });

  const removeAccount = useMutation({
    mutationFn: deleteSelf,
    onSuccess: async () => {
      await signOut();
    },
    onError: (error: unknown) => {
      toast({ message: t('auth.deleteAccountError'), tone: 'danger' });
      // Sunucunun soyledigi sey de kaybolmasin.
      const detail = authErrorMessage(error, t);
      if (detail !== t('auth.deleteAccountError')) {
        toast({ message: detail, tone: 'danger' });
      }
    },
  });

  const onSubmit = handleSubmit((formValues) => {
    const fields = {} as Record<ProfileFieldKey, string>;
    for (const field of PROFILE_FIELDS) fields[field.key] = formValues[field.key].trim();
    save.mutate({
      fields,
      userPassword: formValues.userPassword === '' ? null : formValues.userPassword,
    });
  });

  const organization = snapshot?.organization ?? null;

  return (
    <div className={styles.page}>
      <Card padding="lg">
        <div className={styles.identity}>
          {/* Salt dekoratif: ayni ad hemen yaninda yazili. */}
          <span className={styles.avatarRing} aria-hidden="true">
            <span className={styles.avatar}>
              {initialsOf(snapshot?.user.userFullName ?? '')}
            </span>
          </span>
          <div className={styles.identityText}>
            <h1 className={styles.title}>{snapshot?.user.userFullName ?? ''}</h1>
            <p className={styles.muted}>{snapshot?.user.userEmail ?? ''}</p>
            {/* Isletme ADI ve ROLU burada kaliyor: ikisi de birer BILGI, menude
                karsiligi yok. Kaldirilan sey yalnizca menuyu tekrarlayan
                baglanti listesiydi. */}
            {organization ? (
              <p className={styles.orgLine}>
                <span className={styles.orgName}>{organization.name ?? ''}</span>
                {snapshot?.memberType ? (
                  <Chip tone="primary" className={styles.roleBadge}>
                    {snapshot.memberType === 'admin'
                      ? t('members.types.admin')
                      : t('members.types.staff')}
                  </Chip>
                ) : null}
              </p>
            ) : null}
          </div>
        </div>
      </Card>

      <Card title={t('profile.myInfo')} padding="lg">
        {query.isPending ? <Skeleton shape="block" height="320px" /> : null}

        {query.isError ? (
          <EmptyState
            tone="error"
            title={t('auth.updateError')}
            description={authErrorMessage(query.error, t)}
            action={
              <Button
                tone="neutral"
                onClick={() => {
                  void query.refetch();
                }}
              >
                {t('common.retry')}
              </Button>
            }
          />
        ) : null}

        {query.isSuccess ? (
          <form className={styles.form} onSubmit={(event) => void onSubmit(event)} noValidate>
            <div className={styles.formGrid}>
              {PROFILE_FIELDS.map((field) => {
                // Il/ilce serbest metin degil secici (M4): il alaninin yerinde ikisi birden.
                if (field.key === 'userDistrict') return null;
                if (field.key === 'userCity') {
                  return (
                    <LocationFields
                      key="location"
                      control={control}
                      register={register}
                      setValue={setValue}
                      cityName="userCity"
                      districtName="userDistrict"
                      cityLabel={t('fields.userCity')}
                      districtLabel={t('fields.userDistrict')}
                      required
                    />
                  );
                }
                return (
                  <FormField key={field.key} label={t(field.labelKey)} error={errors[field.key]} required>
                    <Input
                      {...(field.key === 'userEmail'
                        ? ({ type: 'email', inputMode: 'email' } as const)
                        : {})}
                      {...(field.key === 'userPhone' ? ({ type: 'tel', inputMode: 'tel' } as const) : {})}
                      {...(field.key === 'userIdentityId' ? ({ inputMode: 'numeric' } as const) : {})}
                      {...register(field.key)}
                    />
                  </FormField>
                );
              })}
            </div>

            <div className={styles.passwordBlock}>
              <p className={styles.sectionLabel}>{t('profile.changePassword')}</p>
              <p className={styles.muted}>{t('profile.changePasswordHint')}</p>

              <div className={styles.formGrid}>
                <FormField label={t('fields.userPassword')} error={errors.userPassword}>
                  <Input
                    type="password"
                    autoComplete="new-password"
                    {...register('userPassword')}
                  />
                </FormField>
                <FormField label={t('fields.userPasswordRepeat')} error={errors.confirmPassword}>
                  <Input
                    type="password"
                    autoComplete="new-password"
                    {...register('confirmPassword')}
                  />
                </FormField>
              </div>

              {/* Kural durumu CANLI ve yalniz sifre yaziliyorken. Kurallar
                  sunucudaki politikanin aynasi (password-policy.ts). */}
              {password !== '' ? (
                <ul className={styles.policy} aria-label={t('auth.passwordPolicy')}>
                  {PASSWORD_RULES.map((rule) => {
                    const met = rule.test(password);
                    return (
                      <li key={rule.id} className={met ? styles.policyOk : styles.policyPending}>
                        {met ? (
                          <Check size={14} aria-hidden="true" />
                        ) : (
                          <X size={14} aria-hidden="true" />
                        )}
                        <span>{t(rule.messageKey, { min: PASSWORD_MIN_LENGTH })}</span>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </div>

            <div className={styles.actions}>
              <Button type="submit" loading={save.isPending}>
                {t('profile.update')}
              </Button>
            </div>
          </form>
        ) : null}
      </Card>

      <Card title={t('profile.app')} padding="lg">
        <div className={styles.actions}>
          <Button
            tone="neutral"
            iconLeft={<LogOut size={16} aria-hidden="true" />}
            onClick={() => {
              void signOut();
            }}
          >
            {t('actions.logout')}
          </Button>
          <Button
            tone="danger"
            iconLeft={<Trash2 size={16} aria-hidden="true" />}
            onClick={() => {
              setDeleteOpen(true);
            }}
          >
            {t('profile.deleteAccount')}
          </Button>
        </div>
        <p className={styles.version}>
          {t('profile.version')} {env.VITE_APP_VERSION}
        </p>
      </Card>

      {/* Geri alinamaz ve magaza sarti olan bir islem: metnin TAMAMI okunmali,
          bu yuzden tek satirlik bir onay degil tam metinli bir diyalog. */}
      <Dialog
        open={deleteOpen}
        tone="danger"
        title={t('profile.deleteTitle')}
        description={t('profile.deleteDescription')}
        confirmLabel={t('profile.deleteAccount')}
        cancelLabel={t('auth.give')}
        busy={removeAccount.isPending}
        onCancel={() => {
          setDeleteOpen(false);
        }}
        onConfirm={() => {
          setDeleteOpen(false);
          removeAccount.mutate();
        }}
      />
    </div>
  );
}
